if (!customElements.get('maoni-set-configurator')) {
  class MaoniSetConfigurator extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.productRoot = this.closest('product-form');
      this.productForm = null;
      this.boundProductForm = null;
      this.errorElement = this.querySelector('[data-maoni-error]');
      this.selectedList = this.querySelector('[data-maoni-selected-list]');
      this.selectedWrap = this.querySelector('[data-maoni-selected-wrap]');
      this.emptySelection = this.querySelector('[data-maoni-empty-selection]');
      this.disclosure = this.querySelector('[data-maoni-disclosure]');
      this.disclosureToggle = this.disclosure?.querySelector('summary') || null;
      this.toggleStatus = this.querySelector('[data-maoni-toggle-status]');
      this.teaserMeta = this.querySelector('[data-maoni-teaser-meta]');
      this.teaserTitle = this.querySelector('[data-maoni-teaser-title]');
      this.teaserBenefit = this.querySelector('[data-maoni-teaser-benefit]');
      this.teaserBenefitTitle = this.querySelector('[data-maoni-teaser-benefit-title]');
      this.teaserBenefitDetail = this.querySelector('[data-maoni-teaser-benefit-detail]');
      this.currency = this.dataset.currency || 'EUR';
      this.configuratorName = this.dataset.configuratorName || 'MAONI Konfigurator';
      this.configuratorVersion = this.dataset.configuratorVersion || '2026-09-15-compact-open-v5';
      this.sourceProductTitle = this.dataset.productTitle || '';
      this.sourceProductHandle = this.dataset.productHandle || '';
      this.analyticsOpenPublished = false;

      this.bagVariants = this.readBagVariants();
      this.currentBagVariant = this.findBagVariant(this.getCurrentBagVariantId()) || this.bagVariants[0];

      if (this.classList.contains('maoni-configurator--always-open') && this.disclosure) {
        this.disclosure.open = true;
        this.disclosureToggle?.setAttribute('aria-disabled', 'true');
        this.disclosureToggle?.addEventListener('click', (event) => event.preventDefault());
      }

      this.querySelectorAll('input[data-maoni-accessory], input[data-maoni-no-accessory]').forEach((input) => {
        input.addEventListener('change', () => {
          this.syncNoAccessoryState(input);
          this.updateSummary();
          this.publishAccessoryChange(input);
        });
      });

      this.disclosure?.addEventListener('toggle', () => {
        if (this.classList.contains('maoni-configurator--always-open') && !this.disclosure.open) {
          this.disclosure.open = true;
          return;
        }
        if (!this.disclosure.open || this.analyticsOpenPublished) return;
        this.analyticsOpenPublished = true;
        this.publishAnalyticsEvent('open');
      });

      this.productRoot?.addEventListener('on:variant:change', (event) => {
        if (!event.detail?.variant) return;
        this.currentBagVariant = event.detail.variant;
        this.updateSummary();
      });

      if (this.teaserMeta && typeof MutationObserver !== 'undefined') {
        this.teaserObserver = new MutationObserver(() => this.scheduleOptimizedTeaserUpdate());
        this.teaserObserver.observe(this, {
          attributes: true,
          subtree: true,
          attributeFilter: ['hidden', 'disabled', 'data-maoni-active-variant-group']
        });
      }

      // Der vorhandene Symmetry-Kaufbutton bleibt der einzige Kaufbutton.
      // Nur wenn tatsächlich Extras ausgewählt sind, übernehmen wir die Form-Absendung.
      this.bindProductForm();
      this.updateSummary();
      this.publishAnalyticsEvent('view');
      if (this.disclosure?.open && !this.analyticsOpenPublished) {
        this.analyticsOpenPublished = true;
        this.publishAnalyticsEvent('open');
      }
    }

    readBagVariants() {
      const script = this.querySelector('[data-maoni-bag-variants]');
      if (!script) return [];

      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        console.error('MAONI Konfigurator: Varianten konnten nicht gelesen werden.', error);
        return [];
      }
    }

    bindProductForm() {
      const currentForm = this.productRoot?.querySelector('form.js-product-form') || null;
      if (!currentForm) return;

      this.productForm = currentForm;
      if (this.boundProductForm === currentForm) return;

      currentForm.addEventListener('submit', (event) => this.handleProductSubmit(event), true);
      this.boundProductForm = currentForm;
    }

    refreshNativeElements() {
      this.bindProductForm();
      this.nativeSubmitButton = this.productForm?.querySelector('[name="add"]') || null;
      this.dynamicPaymentButtons = Array.from(
        this.productForm?.querySelectorAll('.product-dynamic-payment-button') || []
      );
    }

    getCurrentBagVariantId() {
      this.refreshNativeElements();
      const formVariantInput = this.productForm?.querySelector('input[name="id"]');
      return Number(formVariantInput?.value || this.dataset.initialVariantId || 0);
    }

    getCurrentQuantity() {
      const quantityInput = this.productForm?.querySelector('input[name="quantity"]');
      return Math.max(1, Number(quantityInput?.value || 1));
    }

    findBagVariant(id) {
      return this.bagVariants.find((variant) => Number(variant.id) === Number(id));
    }

    selectedAccessories(type = '') {
      const selector = type
        ? `input[data-maoni-accessory="${type}"]:checked`
        : 'input[data-maoni-accessory]:checked';
      return Array.from(this.querySelectorAll(selector));
    }

    analyticsPayload(extra = {}) {
      return {
        configurator_name: this.configuratorName,
        configurator_version: this.configuratorVersion,
        product_handle: this.sourceProductHandle,
        product_title: this.sourceProductTitle,
        bag_variant_id: Number(this.currentBagVariant?.id || 0),
        selected_accessory_count: this.selectedAccessories().length,
        page_path: window.location.pathname,
        ...extra
      };
    }

    publishAnalyticsEvent(eventName, extra = {}) {
      try {
        window.dispatchEvent(new CustomEvent('maoni:configurator-analytics', {
          detail: {
            name: `maoni:configurator_${eventName}`,
            payload: this.analyticsPayload(extra)
          }
        }));
      } catch (error) {
        // Analytics darf den Kaufablauf niemals blockieren.
      }
    }

    publishAccessoryChange(input) {
      if (input.matches('input[data-maoni-accessory]')) {
        this.publishAnalyticsEvent(input.checked ? 'select' : 'remove', {
          accessory_type: input.dataset.maoniAccessory || 'extra',
          accessory_product: input.dataset.productTitle || '',
          accessory_variant: input.dataset.variantTitle || '',
          accessory_variant_id: Number(input.value || 0),
          selection_action: input.checked ? 'selected' : 'removed'
        });
        return;
      }

      if (input.matches('input[data-maoni-no-accessory]') && input.checked) {
        this.publishAnalyticsEvent('remove', {
          accessory_type: input.dataset.maoniNoAccessory || 'extra',
          selection_action: 'removed_all'
        });
      }
    }

    syncNoAccessoryState(changedInput) {
      const type = changedInput.dataset.maoniAccessory || changedInput.dataset.maoniNoAccessory || '';
      if (!type) return;

      const allAccessoryInputs = Array.from(this.querySelectorAll('input[data-maoni-accessory]'));
      const accessoryInputs = type === 'all'
        ? allAccessoryInputs
        : allAccessoryInputs.filter((input) => input.dataset.maoniAccessory === type);
      const noAccessoryInputs = Array.from(this.querySelectorAll('input[data-maoni-no-accessory]'));
      const noAccessoryInput = noAccessoryInputs.find((input) => input.dataset.maoniNoAccessory === type);
      const globalNoAccessoryInput = noAccessoryInputs.find((input) => input.dataset.maoniNoAccessory === 'all');

      if (changedInput.matches('input[data-maoni-accessory][data-maoni-single-select]') && changedInput.checked) {
        accessoryInputs.forEach((input) => {
          if (input !== changedInput) input.checked = false;
        });
      }

      if (changedInput.matches('input[data-maoni-no-accessory]') && changedInput.checked) {
        accessoryInputs.forEach((input) => {
          input.checked = false;
        });
      } else if (changedInput.matches('input[data-maoni-accessory]') && changedInput.checked) {
        if (noAccessoryInput) noAccessoryInput.checked = false;
        if (globalNoAccessoryInput) globalNoAccessoryInput.checked = false;
      }

      if (noAccessoryInput && type !== 'all' && accessoryInputs.every((input) => !input.checked)) {
        noAccessoryInput.checked = true;
      }

      this.syncGlobalNoAccessoryState();
    }

    syncGlobalNoAccessoryState() {
      const globalNoAccessoryInput = Array.from(this.querySelectorAll('input[data-maoni-no-accessory]'))
        .find((input) => input.dataset.maoniNoAccessory === 'all');
      if (!globalNoAccessoryInput) return;

      const hasSelectedAccessory = Array.from(this.querySelectorAll('input[data-maoni-accessory]'))
        .some((input) => input.checked);
      globalNoAccessoryInput.checked = !hasSelectedAccessory;
    }

    formatMoney(cents) {
      const locale = document.documentElement.lang || 'de-DE';

      try {
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency: this.currency
        }).format(Number(cents || 0) / 100);
      } catch (error) {
        return `${(Number(cents || 0) / 100).toFixed(2).replace('.', ',')} €`;
      }
    }

    accessoryDisplayPrice(input) {
      const originalPrice = Number(input?.dataset?.price || 0);
      const discountedPrice = Number(input?.dataset?.discountedPrice || originalPrice);
      return discountedPrice >= 0 && discountedPrice < originalPrice ? discountedPrice : originalPrice;
    }

    hasHiddenAncestor(element) {
      let current = element;
      while (current && current !== this) {
        if (current.hidden) return true;
        current = current.parentElement;
      }
      return false;
    }

    visibleTeaserAccessories(type = '') {
      const inputs = Array.from(this.querySelectorAll('input[data-maoni-accessory]'))
        .filter((input) => !input.disabled && !this.hasHiddenAncestor(input));
      if (!type) return inputs;

      const typed = inputs.filter((input) => input.dataset.maoniAccessory === type);
      return typed.length > 0 ? typed : inputs;
    }

    teaserColorLabel() {
      const key = this.dataset.maoniActiveVariantGroup || '';
      return ({
        schwarz: 'Schwarz',
        taupe: 'Taupe',
        dunkeltaupe: 'Dunkeltaupe',
        dunkelgrau: 'Dunkelgrau',
        dunkelblau: 'Dunkelblau',
        beige: 'Beige',
        cognac: 'Cognac',
        braun: 'Braun',
        grau: 'Grau',
        beige_gold: 'Beige Gold',
        schwarz_gold: 'Schwarz Gold',
        taupe_gold: 'Taupe Gold',
        dunkeltaupe_gold: 'Dunkeltaupe Gold',
        cognac_gold: 'Cognac Gold',
        hellgrau: 'Hellgrau',
        hellgrau_gold: 'Hellgrau Gold',
        metallic_bronze: 'Metallic Bronze',
        bordeaux: 'Bordeaux',
        burgunder: 'Burgunder',
        khaki: 'Khaki',
        cord_schwarz_gold: 'Schwarz Gold',
        cord_beige_gold: 'Beige Gold',
        cord_umhang_dunkeltaupe_gold: 'Dunkeltaupe Gold',
        cord_umhang_beige_gold: 'Beige Gold'
      })[key] || '';
    }

    scheduleOptimizedTeaserUpdate() {
      if (this.teaserUpdateScheduled) return;
      this.teaserUpdateScheduled = true;
      const update = () => {
        this.teaserUpdateScheduled = false;
        this.updateOptimizedTeaser();
      };
      if (typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(update);
      } else {
        window.setTimeout(update, 0);
      }
    }

    updateOptimizedTeaser() {
      if (!this.teaserMeta) return;

      const type = this.teaserMeta.dataset.maoniTeaserAccessoryType || '';
      const inputs = this.visibleTeaserAccessories(type);
      const allInputs = this.visibleTeaserAccessories();
      const availableTypes = new Set(allInputs.map((input) => input.dataset.maoniAccessory || 'extra'));
      const accessoryTypes = ['strap', 'wallet', 'etui', 'extra'].filter((accessoryType) => availableTypes.has(accessoryType));
      const hasMixedAccessoryTypes = accessoryTypes.length > 1;
      const metaInputs = hasMixedAccessoryTypes ? allInputs : inputs;
      const singular = this.teaserMeta.dataset.maoniTeaserItemSingular || 'Extra';
      const plural = this.teaserMeta.dataset.maoniTeaserItemPlural || 'Extras';
      const fallback = this.teaserMeta.dataset.maoniTeaserFallback || 'Passende Auswahl ansehen';
      const typeLabels = {
        strap: 'Gurte',
        wallet: 'Geldbeutel',
        etui: 'Etuis',
        extra: 'Extras'
      };
      const joinLabels = (labels) => {
        if (labels.length <= 1) return labels[0] || 'Extras';
        return `${labels.slice(0, -1).join(', ')} & ${labels[labels.length - 1]}`;
      };

      if (metaInputs.length === 0) {
        this.teaserMeta.textContent = fallback;
      } else {
        const noun = hasMixedAccessoryTypes ? 'Extras' : (metaInputs.length === 1 ? singular : plural);
        const color = this.teaserMeta.dataset.maoniTeaserShowColor === 'true' ? this.teaserColorLabel() : '';
        const colorSuffix = color ? ` für ${color}` : '';
        this.teaserMeta.textContent = `${metaInputs.length} passende ${noun}${colorSuffix}`;
      }

      const labels = accessoryTypes.map((accessoryType) => typeLabels[accessoryType] || 'Extras');
      if (this.teaserTitle) {
        this.teaserTitle.textContent = `${joinLabels(labels)} passend kombinieren`;
      }

      if (!this.teaserBenefit) return;
      const hasStraps = accessoryTypes.includes('strap');
      const regularPriceLabels = accessoryTypes
        .filter((accessoryType) => accessoryType !== 'strap')
        .map((accessoryType) => typeLabels[accessoryType] || 'Extras');

      if (hasStraps) {
        if (this.teaserBenefitTitle) this.teaserBenefitTitle.textContent = '50 % Gurt-Rabatt';
        if (this.teaserBenefitDetail) {
          this.teaserBenefitDetail.textContent = regularPriceLabels.length > 0
            ? ` · ${joinLabels(regularPriceLabels)} zum regulären Preis`
            : ' beim Kauf einer Tasche';
        }
      } else {
        if (this.teaserBenefitTitle) {
          this.teaserBenefitTitle.textContent = `${joinLabels(regularPriceLabels)} zum regulären Preis`;
        }
        if (this.teaserBenefitDetail) this.teaserBenefitDetail.textContent = '';
      }
    }

    positionWalletCards() {
      const wallets = Array.from(this.querySelectorAll('[data-maoni-wallet-position]'));
      const containers = Array.from(new Set(wallets.map((wallet) => wallet.parentElement).filter(Boolean)));

      containers.forEach((container) => {
        const containerWallets = wallets
          .filter((wallet) => wallet.parentElement === container)
          .sort((left, right) => Number(left.dataset.maoniWalletPosition) - Number(right.dataset.maoniWalletPosition));

        containerWallets.forEach((wallet) => container.removeChild(wallet));
        containerWallets.forEach((wallet) => {
          const position = Math.max(1, Number(wallet.dataset.maoniWalletPosition || 1));
          const visibleAccessories = Array.from(container.children)
            .filter((card) => !card.hidden && card.querySelector('input[data-maoni-accessory]'));
          const target = visibleAccessories[position - 1] || null;
          container.insertBefore(wallet, target);
        });
      });
    }

    positionNoAccessoryCard() {
      const noAccessoryInput = this.querySelector('input[data-maoni-no-accessory="all"]');
      const card = noAccessoryInput?.closest('label');
      const container = card?.parentElement;
      if (card && container) container.appendChild(card);
    }

    normalizeWalletSize(value) {
      return String(value || '')
        .toLocaleLowerCase('de-DE')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/ß/g, 'ss')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
    }

    updateWalletAvailability() {
      const variant = this.currentBagVariant || {};
      const optionValues = [
        ...(Array.isArray(variant.options) ? variant.options : []),
        variant.option1,
        variant.option2,
        variant.option3
      ].filter(Boolean).map((value) => this.normalizeWalletSize(value));

      this.querySelectorAll('[data-maoni-wallet-bag-sizes]').forEach((card) => {
        const allowedSizes = String(card.dataset.maoniWalletBagSizes || '')
          .split('|')
          .map((value) => this.normalizeWalletSize(value))
          .filter(Boolean);
        const sizeMatches = allowedSizes.length === 0 || optionValues.some((optionValue) =>
          allowedSizes.some((allowedSize) => optionValue === allowedSize || optionValue.startsWith(`${allowedSize} `))
        );
        const groupKey = card.dataset.maoniVariantGroup || '';
        const groupMatches = !groupKey || groupKey === (this.dataset.maoniActiveVariantGroup || '');
        const input = card.querySelector('input[data-maoni-accessory="wallet"]');

        card.hidden = !sizeMatches || !groupMatches;
        if (input) {
          input.disabled = !sizeMatches || !groupMatches;
          if (input.disabled) input.checked = false;
        }
      });
    }

    updateSummary() {
      this.refreshNativeElements();

      const freshVariant = this.findBagVariant(this.getCurrentBagVariantId());
      if (freshVariant) this.currentBagVariant = freshVariant;

      const bag = this.currentBagVariant;
      this.updateWalletAvailability();
      this.syncGlobalNoAccessoryState();
      const accessories = this.selectedAccessories();

      const bagTitle = this.querySelector('[data-maoni-bag-variant-title]');
      const bagPrice = this.querySelector('[data-maoni-bag-price]');
      if (bagTitle) bagTitle.textContent = bag?.title || '';
      if (bagPrice) bagPrice.textContent = this.formatMoney(bag?.price || 0);

      this.renderSelectedAccessories(accessories);
      this.updateSelectionCounts();

      const accessoriesTotal = accessories.reduce((sum, input) => sum + this.accessoryDisplayPrice(input), 0);
      const total = Number(bag?.price || 0) + accessoriesTotal;
      const totalElement = this.querySelector('[data-maoni-total]');
      if (totalElement) totalElement.textContent = this.formatMoney(total);

      const discountHint = this.querySelector('[data-maoni-discount-hint]');
      if (discountHint) discountHint.hidden = this.selectedAccessories('strap').length === 0;

      if (this.toggleStatus) {
        this.toggleStatus.textContent = accessories.length > 0
          ? `${accessories.length} ${accessories.length === 1 ? 'Extra' : 'Extras'} · ${this.formatMoney(total)}`
          : 'Optional';
      }

      this.positionWalletCards();
      this.positionNoAccessoryCard();
      this.updateOptimizedTeaser();

      const hasAccessories = accessories.length > 0;
      this.dynamicPaymentButtons.forEach((button) => {
        button.hidden = hasAccessories;
      });

      if (this.nativeSubmitButton) {
        const originalText = this.nativeSubmitButton.dataset.maoniOriginalText
          || this.nativeSubmitButton.dataset.addToCartText
          || this.nativeSubmitButton.textContent.trim()
          || 'In den Warenkorb';

        this.nativeSubmitButton.dataset.maoniOriginalText = originalText;
        if (!this.nativeSubmitButton.classList.contains('is-loading') && bag?.available !== false && !this.nativeSubmitButton.disabled) {
          this.nativeSubmitButton.textContent = hasAccessories ? 'Auswahl in den Warenkorb' : originalText;
        }
      }
    }

    renderSelectedAccessories(accessories) {
      if (!this.selectedList) return;

      this.selectedList.querySelectorAll('[data-maoni-summary-accessory]').forEach((item) => item.remove());
      if (this.emptySelection) this.emptySelection.hidden = accessories.length > 0;
      if (this.selectedWrap) this.selectedWrap.hidden = accessories.length === 0;

      accessories.forEach((input) => {
        const row = document.createElement('div');
        row.className = 'maoni-configurator__summary-line';
        row.dataset.maoniSummaryAccessory = '';

        const description = document.createElement('span');
        const title = document.createElement('span');
        title.textContent = input.dataset.productTitle || '';
        const variant = document.createElement('small');
        variant.textContent = input.dataset.variantTitle || '';
        description.append(title, variant);

        const originalPrice = Number(input.dataset.price || 0);
        const displayedPrice = this.accessoryDisplayPrice(input);
        const price = document.createElement('span');
        price.className = 'maoni-configurator__summary-price';

        if (displayedPrice < originalPrice) {
          const compareAt = document.createElement('s');
          compareAt.textContent = this.formatMoney(originalPrice);
          const discounted = document.createElement('strong');
          discounted.textContent = this.formatMoney(displayedPrice);
          price.append(compareAt, discounted);
        } else {
          const regular = document.createElement('strong');
          regular.textContent = this.formatMoney(originalPrice);
          price.append(regular);
        }

        row.append(description, price);
        this.selectedList.append(row);
      });
    }

    updateSelectionCounts() {
      ['strap', 'wallet', 'etui', 'extra'].forEach((type) => {
        const count = this.selectedAccessories(type).length;
        const element = this.querySelector(`[data-maoni-selected-count="${type}"]`);
        if (element) element.textContent = `${count} gewählt`;
      });
    }

    setError(message = '') {
      if (this.errorElement) {
        this.errorElement.textContent = message;
        this.errorElement.hidden = !message;
      }

      const nativeError = this.productForm?.querySelector('.js-form-error');
      if (nativeError) {
        nativeError.textContent = message;
        nativeError.hidden = !message;
      }
    }

    cartUrl() {
      const root = window.Shopify?.routes?.root || '/';
      return `${root}cart`;
    }

    cartAddUrl() {
      const root = window.Shopify?.routes?.root || '/';
      return `${root}cart/add.js`;
    }

    accessoryTrackingProperties(setId, input) {
      const accessoryLabel = input.dataset.accessoryLabel || 'Ergänzung';
      const properties = {
        '_Konfigurator': this.configuratorName,
        '_Konfigurator-Version': this.configuratorVersion,
        '_Konfigurator-ID': setId,
        '_Konfigurator verwendet': 'Ja',
        '_Zusatzprodukt über Konfigurator': accessoryLabel,
        '_Über Konfigurator gekauft': 'Ja'
      };

      if (this.sourceProductTitle) properties['_Konfigurator-Produkt'] = this.sourceProductTitle;
      if (this.sourceProductHandle) properties['_Konfigurator-Handle'] = this.sourceProductHandle;

      return properties;
    }

    handleProductSubmit(event) {
      const accessories = this.selectedAccessories();

      // Ohne Extras greift der Konfigurator nicht ein. Symmetry verarbeitet den normalen Kauf unverändert.
      if (accessories.length === 0) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      if (typeof this.productRoot?.validate === 'function' && !this.productRoot.validate()) return;
      this.addConfiguredSelectionToCart(accessories);
    }

    setNativeButtonLoading(isLoading) {
      this.refreshNativeElements();
      if (!this.nativeSubmitButton) return;

      if (isLoading) {
        this.nativeSubmitButton.setAttribute('aria-disabled', 'true');
        this.nativeSubmitButton.classList.add('is-loading');
        this.nativeSubmitButton.textContent = 'Auswahl wird hinzugefügt …';
      } else {
        this.nativeSubmitButton.removeAttribute('aria-disabled');
        this.nativeSubmitButton.classList.remove('is-loading');
        this.nativeSubmitButton.textContent = this.selectedAccessories().length > 0
          ? 'Auswahl in den Warenkorb'
          : (this.nativeSubmitButton.dataset.maoniOriginalText || 'In den Warenkorb');
      }
    }

    async addConfiguredSelectionToCart(accessories) {
      this.setError('');

      const freshVariant = this.findBagVariant(this.getCurrentBagVariantId());
      if (freshVariant) this.currentBagVariant = freshVariant;

      const bag = this.currentBagVariant;
      if (!bag?.id) {
        this.setError('Bitte wähle zuerst eine Taschengröße aus.');
        return;
      }

      if (bag.available === false) {
        this.setError('Die gewählte Taschengröße ist aktuell ausverkauft.');
        return;
      }

      const setId = `MAONI-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`;
      const items = [{
        id: Number(bag.id),
        quantity: this.getCurrentQuantity()
      }];

      accessories.forEach((input) => {
        items.push({
          id: Number(input.value),
          quantity: 1,
          properties: this.accessoryTrackingProperties(setId, input)
        });
      });

      const sectionsToUpdate = ['page-header', 'cart-drawer']
        .map((selector) => document.querySelector(selector))
        .filter((element) => element);
      const sectionIds = sectionsToUpdate.map((element) => element.dataset.sectionId).filter(Boolean);

      this.setNativeButtonLoading(true);

      try {
        const response = await fetch(this.cartAddUrl(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items,
            sections: sectionIds.join(','),
            sections_url: window.location.pathname
          })
        });

        const result = await response.json();
        if (!response.ok) {
          throw new Error(result.description || result.message || 'Die Auswahl konnte nicht hinzugefügt werden.');
        }

        const accessoryValue = accessories.reduce((sum, input) => sum + this.accessoryDisplayPrice(input), 0);
        const bagValue = Number(bag.price || 0) * this.getCurrentQuantity();
        const accessoryTypes = [...new Set(accessories.map((input) => input.dataset.maoniAccessory || 'extra'))];
        this.publishAnalyticsEvent('add_to_cart', {
          accessory_count: accessories.length,
          accessory_types: accessoryTypes.join(','),
          currency: this.currency,
          value: (bagValue + accessoryValue) / 100
        });

        if (document.querySelector('.template-cart')) {
          const cartForm = document.querySelector('cart-form');
          if (cartForm?.enableAjaxUpdate) cartForm.refresh();
        } else if (window.theme?.settings?.afterAddToCart === 'page') {
          window.location.assign(this.cartUrl());
          return;
        } else {
          if (result.sections && sectionsToUpdate.length === Object.keys(result.sections).length) {
            sectionsToUpdate.forEach((element) => {
              if (typeof element.updateFromCartChange === 'function') {
                element.updateFromCartChange(result.sections[element.dataset.sectionId]);
              }
            });
          } else {
            this.productRoot?.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
          }

          if (window.theme?.settings?.afterAddToCart === 'drawer') {
            document.querySelector('.js-cart-drawer')?.open(this.nativeSubmitButton);
          } else if (window.theme?.settings?.afterAddToCart === 'notification') {
            const template = document.getElementById('AddedNotification');
            if (template?.content?.firstElementChild) {
              const notification = template.content.firstElementChild.cloneNode(true);
              notification.dataset.productTitle = this.sourceProductTitle || 'Auswahl';
              const container = document.querySelector('.pageheader--sticky') || document.body;
              container.appendChild(notification);
            }
          }
        }

        this.productRoot?.dispatchEvent(new CustomEvent('on:cart:add', {
          bubbles: true,
          detail: { variantId: bag.id }
        }));

        this.nativeSubmitButton?.classList.add('is-success');
        setTimeout(() => {
          this.nativeSubmitButton?.classList.remove('is-success');
          this.setNativeButtonLoading(false);
        }, 1800);
      } catch (error) {
        console.error('MAONI Konfigurator:', error);
        this.setError(error.message || 'Die Auswahl konnte nicht hinzugefügt werden. Bitte versuche es erneut.');
        this.setNativeButtonLoading(false);
      }
    }
  }

  customElements.define('maoni-set-configurator', MaoniSetConfigurator);
}
